/**
 * Shopify Shoppable UGC Gallery Web Component
 * Standard ES6 Class encapsulating Carousel, Player, and AJAX Shopify checkout logic.
 */

if (!customElements.get('shoppable-ugc')) {
  class ShoppableUGC extends HTMLElement {
    constructor() {
      super();
      this.currentVideoIndex = 0;
      this.videoData = [];
      this.selectedOptions = {};
      this.currentProductData = null;
    }

    connectedCallback() {
      // Elements
      this.carousel = this.querySelector('.shoppable-ugc__carousel');
      this.prevBtn = this.querySelector('.shoppable-ugc__carousel-btn--prev');
      this.nextBtn = this.querySelector('.shoppable-ugc__carousel-btn--next');
      this.modal = this.querySelector('.shoppable-ugc__modal');
      this.modalClose = this.querySelector('.shoppable-ugc__modal-close');
      this.modalOverlay = this.querySelector('.shoppable-ugc__modal-overlay');
      this.modalVideo = this.querySelector('.shoppable-ugc__video');
      this.modalPrevBtn = this.querySelector('.shoppable-ugc__nav-btn--prev');
      this.modalNextBtn = this.querySelector('.shoppable-ugc__nav-btn--next');
      
      // Control buttons inside modal
      this.playToggle = this.querySelector('.shoppable-ugc__control-btn--play');
      this.muteToggle = this.querySelector('.shoppable-ugc__control-btn--mute');
      
      // Product Panel Elements
      this.productPanel = this.querySelector('.shoppable-ugc__product-card');
      this.productImage = this.querySelector('.shoppable-ugc__product-image');
      this.productTitle = this.querySelector('.shoppable-ugc__product-title');
      this.productPrice = this.querySelector('.shoppable-ugc__product-price');
      this.productComparePrice = this.querySelector('.shoppable-ugc__product-compare-price');
      this.productOptionsContainer = this.querySelector('.shoppable-ugc__product-options');
      this.atcBtn = this.querySelector('.shoppable-ugc__atc-btn');
      this.atcBtnText = this.querySelector('.shoppable-ugc__atc-btn-text');
      this.atcBtnSpinner = this.querySelector('.shoppable-ugc__atc-btn-spinner');
      this.productLink = this.querySelector('.shoppable-ugc__view-product-link');
      this.loader = this.querySelector('.shoppable-ugc__player-loader');

      // Initialize Data from Cards
      this.initializeVideoData();

      // Listeners
      if (this.prevBtn && this.nextBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollCarousel('prev'));
        this.nextBtn.addEventListener('click', () => this.scrollCarousel('next'));
        this.carousel.addEventListener('scroll', () => this.updateCarouselButtons());
        // Initial button check
        this.updateCarouselButtons();
      }

      // Card Clicks & Intersection Preloading
      this.setupCards();

      // Modal Triggers
      this.modalClose.addEventListener('click', () => this.closeModal());
      this.modalOverlay.addEventListener('click', () => this.closeModal());
      this.modalPrevBtn.addEventListener('click', () => this.navigateVideo('prev'));
      this.modalNextBtn.addEventListener('click', () => this.navigateVideo('next'));

      // Player Controls
      this.playToggle.addEventListener('click', () => this.togglePlayState());
      this.muteToggle.addEventListener('click', () => this.toggleMuteState());

      // Sync button states automatically with native video player events
      this.modalVideo.addEventListener('play', () => this.updatePlayControlsIcon(false));
      this.modalVideo.addEventListener('pause', () => this.updatePlayControlsIcon(true));

      // ATC AJAX Action
      this.atcBtn.addEventListener('click', () => this.handleAddToCart());

      // Key Events
      window.addEventListener('keydown', (e) => this.handleKeyPress(e));

      // Mobile Swipe Gestures inside Modal
      this.setupSwipeGestures();
    }

    initializeVideoData() {
      const cardElements = this.querySelectorAll('.shoppable-ugc__card');
      cardElements.forEach((card, index) => {
        this.videoData.push({
          index: index,
          videoUrl: card.dataset.videoUrl,
          posterUrl: card.dataset.posterUrl,
          productHandle: card.dataset.productHandle,
          title: card.querySelector('.shoppable-ugc__card-video')?.getAttribute('poster') || 'Video'
        });
      });
    }

    setupCards() {
      const cards = this.querySelectorAll('.shoppable-ugc__card');
      const lazyLoadObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const video = entry.target.querySelector('.shoppable-ugc__card-video');
            if (video && !video.src) {
              video.src = video.dataset.src;
            }
          }
        });
      }, { rootMargin: '100px' });

      cards.forEach((card, idx) => {
        lazyLoadObserver.observe(card);

        // Click to Open Modal
        card.addEventListener('click', () => {
          this.openModal(idx);
        });

        // Hover Autoplay Previews (Muted)
        if (this.dataset.autoplayHover === 'true') {
          card.addEventListener('mouseenter', () => {
            const video = card.querySelector('.shoppable-ugc__card-video');
            if (video && video.readyState >= 2) {
              video.play().catch(() => {});
            }
          });
          card.addEventListener('mouseleave', () => {
            const video = card.querySelector('.shoppable-ugc__card-video');
            if (video) {
              video.pause();
            }
          });
        }
      });
    }

    scrollCarousel(direction) {
      const scrollAmount = this.carousel.offsetWidth * 0.75;
      const targetScroll = this.carousel.scrollLeft + (direction === 'next' ? scrollAmount : -scrollAmount);
      this.carousel.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }

    updateCarouselButtons() {
      const tolerance = 5;
      const atStart = this.carousel.scrollLeft <= tolerance;
      const atEnd = this.carousel.scrollLeft + this.carousel.clientWidth >= this.carousel.scrollWidth - tolerance;
      
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
    }

    /* Modal Core Logic */
    openModal(index) {
      this.currentVideoIndex = index;
      this.modal.style.display = 'flex';
      this.modal.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden'; // Lock background scroll
      this.loadVideoInModal(index);
    }

    closeModal() {
      this.modal.style.display = 'none';
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = ''; // Unlock background
      this.modalVideo.pause();
      this.modalVideo.src = '';
    }

    loadVideoInModal(index) {
      const videoInfo = this.videoData[index];
      if (!videoInfo) return;

      this.loader.style.display = 'flex';
      this.modalVideo.src = videoInfo.videoUrl;
      this.modalVideo.poster = videoInfo.posterUrl;
      this.modalVideo.autoplay = true;
      this.modalVideo.muted = true; // browser requires mute for autoplay to succeed
      this.modalVideo.load();
      
      // Auto-play the main video
      const playPromise = this.modalVideo.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.loader.style.display = 'none';
            this.updatePlayControlsIcon(false);
          })
          .catch((error) => {
            console.log("Play failed / was prevented by browser:", error);
            this.loader.style.display = 'none';
            this.updatePlayControlsIcon(true);
          });
      } else {
        this.loader.style.display = 'none';
      }

      // Manage nav arrows inside modal
      this.modalPrevBtn.style.display = index === 0 ? 'none' : 'flex';
      this.modalNextBtn.style.display = index === this.videoData.length - 1 ? 'none' : 'flex';

      // Load Product Info
      this.fetchProductDetails(videoInfo.productHandle);
    }

    navigateVideo(direction) {
      if (direction === 'next' && this.currentVideoIndex < this.videoData.length - 1) {
        this.loadVideoInModal(this.currentVideoIndex + 1);
        this.currentVideoIndex++;
      } else if (direction === 'prev' && this.currentVideoIndex > 0) {
        this.loadVideoInModal(this.currentVideoIndex - 1);
        this.currentVideoIndex--;
      }
    }

    /* Video Player Control Actions */
    togglePlayState() {
      if (this.modalVideo.paused) {
        this.modalVideo.play();
        this.updatePlayControlsIcon(false);
      } else {
        this.modalVideo.pause();
        this.updatePlayControlsIcon(true);
      }
    }

    updatePlayControlsIcon(isPaused) {
      const iconPlay = this.playToggle.querySelector('.icon-play');
      const iconPause = this.playToggle.querySelector('.icon-pause');
      if (isPaused) {
        iconPlay.style.display = 'block';
        iconPause.style.display = 'none';
        this.playToggle.setAttribute('aria-label', 'Play video');
      } else {
        iconPlay.style.display = 'none';
        iconPause.style.display = 'block';
        this.playToggle.setAttribute('aria-label', 'Pause video');
      }
    }

    toggleMuteState() {
      this.modalVideo.muted = !this.modalVideo.muted;
      const iconMuted = this.muteToggle.querySelector('.icon-muted');
      const iconUnmuted = this.muteToggle.querySelector('.icon-unmuted');
      if (this.modalVideo.muted) {
        iconMuted.style.display = 'block';
        iconUnmuted.style.display = 'none';
        this.muteToggle.setAttribute('aria-label', 'Unmute video');
      } else {
        iconMuted.style.display = 'none';
        iconUnmuted.style.display = 'block';
        this.muteToggle.setAttribute('aria-label', 'Mute video');
      }
    }

    /* Product Loading & Variant Selector Options */
    fetchProductDetails(handle) {
      if (!handle) {
        this.productPanel.style.display = 'none';
        return;
      }
      this.productPanel.style.display = 'flex';
      this.atcBtn.disabled = true;
      this.atcBtnText.textContent = 'Loading Product...';

      fetch(`/products/${handle}.js`)
        .then(response => {
          if (!response.ok) throw new Error('Product details could not be retrieved.');
          return response.json();
        })
        .then(product => {
          this.renderProductCard(product);
        })
        .catch(err => {
          console.error(err);
          this.atcBtnText.textContent = 'Error Loading Product';
        });
    }

    renderProductCard(product) {
      this.currentProductData = product;
      this.productImage.src = product.featured_image;
      this.productImage.alt = product.title;
      this.productTitle.textContent = product.title;
      this.productLink.href = `/products/${product.handle}`;

      // Reset Selected Options
      this.selectedOptions = {};
      
      // Setup Options
      this.productOptionsContainer.innerHTML = '';
      if (product.variants.length > 1 && product.options) {
        product.options.forEach(option => {
          // Ignore default title option
          if (option.name === 'Title' && option.values.includes('Default Title')) return;

          const group = document.createElement('div');
          group.className = 'shoppable-ugc__option-group';
          
          const label = document.createElement('span');
          label.className = 'shoppable-ugc__option-label';
          label.textContent = option.name;
          group.appendChild(label);

          const valuesContainer = document.createElement('div');
          valuesContainer.className = 'shoppable-ugc__option-values';

          option.values.forEach((value, index) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = `shoppable-ugc__option-btn${index === 0 ? ' is-active' : ''}`;
            btn.textContent = value;
            
            // Set first value active by default
            if (index === 0) {
              this.selectedOptions[option.name] = value;
            }

            btn.addEventListener('click', () => {
              valuesContainer.querySelectorAll('.shoppable-ugc__option-btn').forEach(b => b.classList.remove('is-active'));
              btn.classList.add('is-active');
              this.selectedOptions[option.name] = value;
              this.updateVariantSelection();
            });

            valuesContainer.appendChild(btn);
          });

          group.appendChild(valuesContainer);
          this.productOptionsContainer.appendChild(group);
        });
      }

      this.updateVariantSelection();
    }

    updateVariantSelection() {
      if (!this.currentProductData) return;

      const variants = this.currentProductData.variants;
      
      // If single default variant, select it
      let selectedVariant = variants[0];

      if (variants.length > 1) {
        selectedVariant = variants.find(variant => {
          return variant.options.every((optionVal, index) => {
            const optionName = this.currentProductData.options[index].name;
            return this.selectedOptions[optionName] === optionVal;
          });
        });
      }

      if (selectedVariant) {
        this.selectedVariantId = selectedVariant.id;
        
        // Update price
        const formattedPrice = this.formatMoney(selectedVariant.price);
        this.productPrice.textContent = formattedPrice;

        if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
          this.productComparePrice.textContent = this.formatMoney(selectedVariant.compare_at_price);
          this.productComparePrice.style.display = 'inline';
        } else {
          this.productComparePrice.style.display = 'none';
        }

        // Update variant images if configured
        if (selectedVariant.featured_image) {
          this.productImage.src = selectedVariant.featured_image.src;
        }

        // Check availability
        if (selectedVariant.available) {
          this.atcBtn.disabled = false;
          
          // Determine if preorder is dynamic (based on product tags or custom handle)
          const isPreorder = this.currentProductData.tags.includes('preorder') || 
                             this.currentProductData.tags.includes('Preorder') ||
                             this.currentProductData.handle.includes('preorder');
          
          this.atcBtnText.textContent = isPreorder ? this.dataset.preorderText : this.dataset.atcText;
        } else {
          this.atcBtn.disabled = true;
          this.atcBtnText.textContent = 'Out of Stock';
        }
      } else {
        this.selectedVariantId = null;
        this.atcBtn.disabled = true;
        this.atcBtnText.textContent = 'Unavailable';
      }
    }

    /* AJAX Shop API Add To Cart */
    handleAddToCart() {
      if (!this.selectedVariantId) return;

      this.atcBtn.disabled = true;
      this.atcBtnSpinner.style.display = 'inline-block';
      this.atcBtnText.style.opacity = '0.3';

      const formData = {
        items: [{
          id: this.selectedVariantId,
          quantity: 1
        }]
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
        .then(response => {
          if (!response.ok) throw new Error('Could not add variant to cart.');
          return response.json();
        })
        .then(item => {
          // Success State feedback
          this.atcBtnSpinner.style.display = 'none';
          this.atcBtnText.style.opacity = '1';
          this.atcBtnText.textContent = 'Added ✓';
          this.atcBtn.classList.add('btn--success');
          
          // Dispatch standard events for theme update
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:update'));
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: item }
          }));

          // Reset button text after 2 seconds
          setTimeout(() => {
            this.atcBtn.classList.remove('btn--success');
            this.atcBtn.disabled = false;
            this.updateVariantSelection();
          }, 2000);
        })
        .catch(err => {
          console.error(err);
          this.atcBtnSpinner.style.display = 'none';
          this.atcBtnText.style.opacity = '1';
          this.atcBtnText.textContent = 'Limit Reached / Error';
          setTimeout(() => {
            this.atcBtn.disabled = false;
            this.updateVariantSelection();
          }, 2500);
        });
    }

    /* Helper Utilities */
    formatMoney(cents) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      
      // Determine currency formatting or fall back to theme configuration
      const themeCurrencySymbol = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) 
        ? this.getCurrencySymbol(window.Shopify.currency.active) 
        : '$';
      
      const dollars = (cents / 100).toFixed(2);
      return `${themeCurrencySymbol}${dollars}`;
    }

    getCurrencySymbol(code) {
      const symbols = { USD: '$', CAD: '$', EUR: '€', GBP: '£', AUD: '$', JPY: '¥' };
      return symbols[code] || '$';
    }

    handleKeyPress(e) {
      if (this.modal.style.display === 'flex') {
        if (e.key === 'Escape') this.closeModal();
        if (e.key === 'ArrowRight') this.navigateVideo('next');
        if (e.key === 'ArrowLeft') this.navigateVideo('prev');
      }
    }

    /* Swipe touch controls for premium mobile experience */
    setupSwipeGestures() {
      let touchStartX = 0;
      let touchEndX = 0;
      
      this.modal.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.modal.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      }, { passive: true });
    }

    handleSwipe(start, end) {
      const swipeDistanceThreshold = 80;
      const distance = end - start;
      
      // Swipe left = next video, swipe right = previous video
      if (Math.abs(distance) > swipeDistanceThreshold) {
        if (distance < 0) {
          this.navigateVideo('next');
        } else {
          this.navigateVideo('prev');
        }
      }
    }
  }

  customElements.define('shoppable-ugc', ShoppableUGC);
}
